import { useQuery } from '@tanstack/react-query'
import { Target } from 'lucide-react'
import { Bar, BarChart, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { accountApi, benchmarkApi, convictionApi, positionsApi, systemApi } from '../api/client'
import { ChartCard } from './ChartCard'
import { useTimeframe } from '../hooks/useTimeframe'
import { METRICS } from '../lib/metrics'
import { buildPickBars, bookWindowReturn } from '../lib/pickPerformance'
import { beatingCount, spyDayEndDate, spyReturnForTimeframe } from '../lib/spyVerdict'
import { timeframeLabel, toEquityRange } from '../lib/timeframe'
import type { Book } from '../types'
import { StatTile } from './StatTile'
import './PicksPanel.css'

interface PicksPanelProps {
  book: Book | undefined   // research book from useBooks; undefined while loading
  booksPending: boolean    // useBooks().isLoading — books roster still in flight
  booksError: boolean      // useBooks().isError — books roster failed to load
}

const fmtUsd = (v: number) =>
  `${v >= 0 ? '+' : '−'}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtPct = (v: number) => `${v < 0 ? '−' : '+'}${Math.abs(v).toFixed(2)}%`

/**
 * Zone 1 (spec §5): "are my picks working?" — verdict tiles + per-pick
 * return bars for the global timeframe. DAY = broker intraday; longer
 * windows = since-entry with per-row excess-vs-SPY chips (the scorecard
 * measure). SPY reference line (DAY only) + excess/beating tiles come from
 * the P2 benchmark endpoint; a benchmark failure or null window degrades to
 * the P1 absolute-only view — never blocks P&L display.
 */
export function PicksPanel({ book, booksPending, booksError }: PicksPanelProps) {
  const { timeframe } = useTimeframe()
  const accountId = book?.id

  const positionsQ = useQuery({
    queryKey: ['positions', accountId],
    queryFn: () => positionsApi.getAll(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  const scorecardQ = useQuery({
    queryKey: ['scorecard', accountId],
    queryFn: () => convictionApi.scorecard(accountId),
    enabled: accountId !== undefined,
    retry: 1,
    refetchInterval: 30000,
  })
  const accountQ = useQuery({
    queryKey: ['account', accountId],
    queryFn: () => accountApi.get(accountId),
    enabled: accountId !== undefined,
    refetchInterval: 10000,
  })
  // Only fetched for windowed (non-DAY) book return; same key shape as the
  // equity charts so react-query dedupes with them.
  const historyQ = useQuery({
    queryKey: ['equity-history', accountId, toEquityRange(timeframe)],
    queryFn: () => accountApi.history(accountId, toEquityRange(timeframe)),
    enabled: accountId !== undefined && timeframe !== 'DAY',
    retry: 1,
    staleTime: 300000,
  })
  // Global, not account-scoped (SPY is the same for every book); slow-moving
  // so a gentle poll is enough. A failure/null here degrades the tiles below
  // to '—' — it never blocks the P1 absolute P&L view.
  const benchQ = useQuery({
    queryKey: ['benchmark-returns'],
    queryFn: () => benchmarkApi.returns(),
    retry: 1,
    staleTime: 60000,
    refetchInterval: 300000,
  })
  // Shares AppShell's cache entry (same key, same queryFn) so this never
  // double-fetches /system/health — used only for the DAY window's "last
  // session" framing below.
  const healthQ = useQuery({
    queryKey: ['system-health'],
    queryFn: () => systemApi.getHealth(),
    refetchInterval: 60000,
  })

  const isLoading = positionsQ.isLoading || scorecardQ.isLoading || accountQ.isLoading
  const isError = positionsQ.isError
  // enabled: accountId !== undefined means the above queries report
  // isLoading=false while the book itself hasn't resolved yet (react-query
  // v5: a disabled query is never "loading"). Without this, an in-flight
  // or failed useBooks() reads as "no held positions" fact rather than
  // unknown state.
  const booksUnresolved = booksPending || (book === undefined && !booksError)

  const { bars, noIntradayCount } = buildPickBars(
    timeframe, positionsQ.data ?? [], scorecardQ.data?.positions ?? [])
  const winners = bars.filter((b) => b.returnPct > 0).length
  const losers = bars.filter((b) => b.returnPct < 0).length

  const spyPct = spyReturnForTimeframe(timeframe, benchQ.data)
  const beating = beatingCount(timeframe, bars, timeframe === 'DAY' ? spyPct : null)
  // IMPORTANT 4: during market hours the DAY window's daily bar often hasn't
  // rolled yet, so `spyPct` can be the PRIOR session's return while the
  // picks it's compared against are live intraday. Label rather than null:
  // when the day window's end date isn't today (viewer-local calendar
  // date), show that date instead of claiming "today".
  const spyDayEnd = timeframe === 'DAY' ? spyDayEndDate(benchQ.data) : null
  const todayLocal = new Date().toLocaleDateString('en-CA')
  const spyDayStale = spyDayEnd !== null && spyDayEnd !== todayLocal

  const account = accountQ.data
  const rawNetFlow = historyQ.data?.net_flow != null ? Number(historyQ.data.net_flow) : null
  const netFlow = rawNetFlow !== null && Number.isFinite(rawNetFlow) ? rawNetFlow : null
  const bookReturn = account
    ? bookWindowReturn(
        timeframe,
        { daily_pnl: Number(account.daily_pnl), account_value: Number(account.account_value) },
        historyQ.data?.points ?? [],
        netFlow)
    : { pct: null, usd: null, flowAdjusted: timeframe === 'DAY' }

  // "today" is only accurate while the market's actually open — after the
  // close it's describing the just-finished session, not the calendar day.
  // Mirrors AppShell's marketClosed convention: unknown/loading health data
  // reads as "open" so a flaky health poll never mislabels a live session.
  const marketStatus = healthQ.data?.market_status
  const lastSession =
    timeframe === 'DAY' && marketStatus !== undefined && marketStatus !== 'open' && marketStatus !== 'unknown'
  const windowNote = timeframe === 'DAY'
    ? (lastSession ? 'last session' : 'today')
    : `since entry · window ${timeframeLabel(timeframe)}`
  // IMPORTANT 3: the tile value below is `bookReturn.pct - spyPct` — the BOOK's
  // window return vs SPY, NOT a per-pick since-entry excess. That's a
  // distinct measure from `excess_vs_spy_since_entry` (which the scorecard's
  // per-row excess chips actually compute), so it gets its own glossary key
  // rather than borrowing that label (was previously mislabeled).
  const excessMetricKey = timeframe === 'DAY' ? 'excess_vs_spy_today' : 'excess_vs_spy_window'
  const excessLabel = timeframe === 'DAY' ? METRICS.excess_vs_spy_today.label : METRICS.excess_vs_spy_window.short
  const excessPct = bookReturn.pct !== null && spyPct !== null ? bookReturn.pct - spyPct : null

  // The tiles row (and the flows-unavailable caption) render unconditionally,
  // including while the panel is loading (they show '—' placeholders) — so
  // they're passed as `preBody`, which ChartCard renders inside the card
  // (after the header) but never gates on isLoading/isError/isEmpty the way
  // it gates `children`.
  const tilesAndCaptions = (
    <>
      <div className="picks-panel-tiles">
        <StatTile
          label={excessLabel}
          metric={excessMetricKey}
          value={excessPct !== null ? fmtPct(excessPct) : '—'}
          tone={excessPct === null ? 'neutral' : excessPct >= 0 ? 'gain' : 'loss'}
          emphasis
        />
        <StatTile
          label={METRICS.book_return.label}
          metric="book_return"
          value={bookReturn.pct !== null ? fmtPct(bookReturn.pct) : '—'}
          sub={bookReturn.usd !== null ? fmtUsd(bookReturn.usd) : undefined}
          tone={bookReturn.pct === null ? 'neutral' : bookReturn.pct >= 0 ? 'gain' : 'loss'}
        />
        <StatTile
          label="SPY"
          value={spyPct !== null ? fmtPct(spyPct) : '—'}
          sub={
            timeframe === 'ALL'
              ? 'no all-time baseline'
              : spyDayStale
                ? (spyDayEnd ?? undefined)
                : undefined
          }
          tone="neutral"
        />
        <StatTile
          label={METRICS.day_pnl.label}
          metric="day_pnl"
          value={account ? fmtUsd(Number(account.daily_pnl)) : '—'}
          sub={lastSession ? 'last session' : undefined}
          tone={!account ? 'neutral' : Number(account.daily_pnl) >= 0 ? 'gain' : 'loss'}
        />
        <StatTile
          label="Winners"
          value={bars.length > 0 ? `${winners} up · ${losers} down` : '—'}
          tone={bars.length === 0 || winners === losers ? 'neutral' : winners > losers ? 'gain' : 'loss'}
        />
        <StatTile
          label="Beating SPY"
          value={beating ? `${beating.beating} of ${beating.of}` : '—'}
          tone={!beating ? 'neutral' : beating.beating * 2 >= beating.of ? 'gain' : 'loss'}
        />
      </div>
      {!booksUnresolved && !isLoading && !historyQ.isPending && bookReturn.pct !== null &&
        !bookReturn.flowAdjusted && (
        <p className="caption">flows unavailable — window return is raw equity change</p>
      )}
    </>
  )

  return (
    <ChartCard
      className="picks-panel"
      title={
        <>
          <Target size={18} className="icon" aria-hidden="true" />
          Picks · {book?.label ?? 'research book'}
        </>
      }
      headerExtra={
        <>
          <span className="caption">{windowNote}</span>
          {benchQ.isError && <span className="caption">SPY unavailable</span>}
          {accountQ.isError && <span className="caption">account data unavailable</span>}
          {scorecardQ.isError && <span className="caption">scorecard unavailable</span>}
        </>
      }
      preBody={tilesAndCaptions}
      isLoading={booksUnresolved || isLoading}
      isError={false}
      isEmpty={booksError || isError || bars.length === 0}
      empty={
        booksError ? (
          <div className="empty-state">
            <div className="empty-icon"><Target size={28} aria-hidden="true" /></div>
            <div className="empty-text">books unavailable</div>
          </div>
        ) : isError ? (
          <div className="empty-state">
            <div className="empty-icon"><Target size={28} aria-hidden="true" /></div>
            <div className="empty-text">positions unavailable</div>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon"><Target size={28} aria-hidden="true" /></div>
            <div className="empty-text">
              {timeframe === 'DAY' && noIntradayCount > 0
                ? `no intraday P&L from the broker (${noIntradayCount} position${noIntradayCount === 1 ? '' : 's'})`
                : 'no held positions'}
            </div>
          </div>
        )
      }
    >
      <ResponsiveContainer width="100%" height={Math.max(180, bars.length * 26)}>
        <BarChart data={bars} layout="vertical" margin={{ left: 8, right: 16 }}>
          <XAxis type="number" stroke="var(--fg-subtle)" fontSize={11} tickFormatter={(v) => `${v}%`} />
          <YAxis type="category" dataKey="symbol" stroke="var(--fg-subtle)" fontSize={11} width={56} />
          <Tooltip
            formatter={(v: number, _name, item) => {
              const bar = item?.payload as { pnlUsd: number; excessSinceEntryPct: number | null }
              const excess = bar.excessSinceEntryPct !== null
                ? ` · excess ${fmtPct(bar.excessSinceEntryPct)}` : ''
              return [`${fmtPct(v)} (${fmtUsd(bar.pnlUsd)})${excess}`, timeframe === 'DAY' ? 'today' : 'since entry']
            }}
          />
          <Bar dataKey="returnPct" radius={[0, 3, 3, 0]}>
            {bars.map((b) => (
              <Cell key={b.symbol} fill={b.returnPct >= 0 ? 'var(--gain)' : 'var(--loss)'} />
            ))}
          </Bar>
          <ReferenceLine x={0} stroke="var(--border-strong)" />
          {timeframe === 'DAY' && spyPct !== null && (
            <ReferenceLine
              x={spyPct}
              stroke="var(--fg-subtle)"
              strokeDasharray="4 3"
              label={{
                value: spyDayStale
                  ? `SPY ${fmtPct(spyPct)} · ${spyDayEnd}`
                  : `SPY ${fmtPct(spyPct)} today`,
                position: 'top', fill: 'var(--fg-subtle)', fontSize: 11,
              }}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
      {timeframe === 'DAY' && noIntradayCount > 0 && (
        <p className="caption picks-panel-note">{noIntradayCount} without intraday data</p>
      )}
    </ChartCard>
  )
}
